import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { startOfDay, endOfDay, isWithinInterval, parse } from 'date-fns';

/** Default timezone used when no timezone is specified */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * Gets the current date/time in the specified timezone
 * @param timezone - IANA timezone identifier (defaults to America/Chicago)
 */
export function getNowInTimezone(timezone: string = DEFAULT_TIMEZONE): Date {
  return toZonedTime(new Date(), timezone);
}

/**
 * @deprecated Use getNowInTimezone() instead
 * Gets the current date/time in Central Time (America/Chicago)
 */
export function getNowInCentralTime(): Date {
  return getNowInTimezone(DEFAULT_TIMEZONE);
}

/**
 * Parses a date string as if it's in the specified timezone and returns start of that day
 * Used for start dates - item becomes active at 12:00 AM in the timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timezone - IANA timezone identifier (defaults to America/Chicago)
 */
export function parseStartDate(dateString: string, timezone: string = DEFAULT_TIMEZONE): Date {
  // Parse the date string as a date in the target timezone (not UTC)
  const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
  // Create a date representing midnight in the target timezone
  const midnightInZone = startOfDay(parsedDate);
  // Convert that timezone-local midnight to UTC for proper comparison
  return fromZonedTime(midnightInZone, timezone);
}

/**
 * @deprecated Use parseStartDate() instead
 * Parses a date string and returns the start of that day in Central Time
 */
export function parseStartDateCT(dateString: string): Date {
  return parseStartDate(dateString, DEFAULT_TIMEZONE);
}

/**
 * Parses a date string as if it's in the specified timezone and returns end of that day
 * Used for end dates - item expires at 11:59:59 PM in the timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timezone - IANA timezone identifier (defaults to America/Chicago)
 */
export function parseEndDate(dateString: string, timezone: string = DEFAULT_TIMEZONE): Date {
  // Parse the date string as a date in the target timezone (not UTC)
  const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
  // Create a date representing end of day in the target timezone
  const endOfDayInZone = endOfDay(parsedDate);
  // Convert that timezone-local end of day to UTC for proper comparison
  return fromZonedTime(endOfDayInZone, timezone);
}

/**
 * @deprecated Use parseEndDate() instead
 * Parses a date string and returns the end of that day in Central Time
 */
export function parseEndDateCT(dateString: string): Date {
  return parseEndDate(dateString, DEFAULT_TIMEZONE);
}

/**
 * Checks if the current time is within the start and end date range in the specified timezone
 * @param startDate - Start date string (YYYY-MM-DD format)
 * @param endDate - End date string (YYYY-MM-DD format)
 * @param timezone - IANA timezone identifier (defaults to America/Chicago)
 * @returns true if currently active, false otherwise
 */
export function isCurrentlyActive(
  startDate: string,
  endDate: string,
  timezone: string = DEFAULT_TIMEZONE
): boolean {
  try {
    const now = new Date(); // Current UTC time
    const start = parseStartDate(startDate, timezone);
    const end = parseEndDate(endDate, timezone);

    return isWithinInterval(now, { start, end });
  } catch {
    // If there's any parsing error, default to showing the item
    return true;
  }
}
